import React from 'react'
import PropTypes from 'prop-types'

import { Form, Glyphicon, Alert } from 'react-bootstrap'
import InputField, { useFormValues } from './../../../FormInputComponents/InputField'

import CategorySuggestion from './../../../Suggestion/CategorySuggestion'
import MerchantSuggestion from './../../../Suggestion/MerchantSuggestion'

import useFormMerchantCategoryFilds from './useFormMerchantCategoryFilds'
import useModalUISettings from './../../Common/useModalUISettings'
import useFormButtons from './../../Common/useFormButtons'
import useMode from './../../Common/useMode'

import NoteFields from './../../Common/NoteFields'
import CommonRuleModalButtons from './../../Common/CommonRuleModalButtons'

export default function MerchantCategoryRuleModal({ value, isRequest, onOkHandler, onRejectHandler, isReadOnly }) {
  const {
    id,
    merchantId,
    merchantName,
    categoryId,
    categoryFullPath,
    request_notes,
    review_notes,
    hasActiveRule
  } = value
  const mode = useMode(isRequest, isReadOnly, id)

  const { values, isFormChanged, handleFieldChange, updateValue } = useFormValues({
    id: id ?? null,
    categoryId: categoryId ?? null,
    categoryFullPath: categoryFullPath ?? null,
    merchantId: merchantId ?? null,
    merchantName: merchantName ?? null,
    request_notes: request_notes ?? null,
    review_notes: review_notes ?? null,
    hasActiveRule: hasActiveRule ?? null
  })

  const [handleMerchantChange, handleCategoryChange] = useFormMerchantCategoryFilds(values, updateValue, mode)

  const isAllFieldsFilled = values.categoryId && values.merchantId
  const fieldsNames = 'Merchant and Category'
  const isRuleActiveFunc = rule => rule.isActive && rule.categoryIsActive && rule.merchantIsActive

  const { ruleInfoMessage, ruleInfoStyle, okButtonName, isOkButtonDisabled } = useModalUISettings({
    prevRuleId: values.id,
    isAllFieldsFilled,
    isFormChanged,
    fieldsNames,
    isRuleActiveFunc,
    mode
  })

  const sentFieldsForRules = ['merchantId', 'categoryId']
  const sentFieldsForRequests = ['merchantName', 'categoryFullPath', 'request_notes', 'review_notes']
  const { handleOkButton, handleRejectButton, handleCancelButton } = useFormButtons({
    onOkHandler,
    onRejectHandler,
    values,
    sentFieldsForRules,
    sentFieldsForRequests,
    isRequest
  })

  const activeRuleAlreadyExistsMessageShown =
    (mode.isRuleCreate || mode.isRequestCreate || mode.isRequestApprove) && values.hasActiveRule
  return (
    <Form className="text-left" horizontal>
      <InputField validationState={values.merchantId === undefined ? 'error' : null} required label={'Merchant'}>
        <MerchantSuggestion
          selectedId={values.merchantId}
          selectedLabel={values.merchantName}
          changeCallback={handleMerchantChange}
          readOnly={mode.isRequestApprove || mode.isRequestView}
        />
      </InputField>
      <InputField validationState={values.categoryId === undefined ? 'error' : null} required label={'Category'}>
        <CategorySuggestion
          selectedId={values.categoryId}
          selectedLabel={values.categoryFullPath}
          changeCallback={handleCategoryChange}
          readOnly={mode.isRequestApprove || mode.isRequestView}
        />
      </InputField>

      {!mode.isRequestView && !values.hasActiveRule && (
        <Alert bsStyle={ruleInfoStyle}>
          <Glyphicon className="padding-rigt-standard" glyph="info-sign" />
          {ruleInfoMessage}
        </Alert>
      )}
      {activeRuleAlreadyExistsMessageShown && (
        <Alert bsStyle={'danger'}>
          <Glyphicon className="padding-rigt-standard" glyph="info-sign" />
          An active MCR rule already exists.
        </Alert>
      )}
      <NoteFields
        mode={mode}
        request_notes={values.request_notes}
        review_notes={values.review_notes}
        handleFieldChange={handleFieldChange}
      />

      <CommonRuleModalButtons
        handleOkButton={handleOkButton}
        handleRejectButton={handleRejectButton}
        handleCancelButton={handleCancelButton}
        mode={mode}
        okButtonName={okButtonName}
        isOkButtonDisabled={isOkButtonDisabled || activeRuleAlreadyExistsMessageShown}
      />
    </Form>
  )
}

MerchantCategoryRuleModal.defaultProps = {
  value: {
    id: null,
    merchantId: undefined,
    merchantName: null,
    categoryId: undefined,
    categoryFullPath: null,
    request_notes: null,
    review_notes: null,
    hasActiveRule: false
  },
  isRequest: false,
  isReadOnly: false
}

MerchantCategoryRuleModal.propTypes = {
  value: PropTypes.shape({
    id: PropTypes.number,
    merchantId: PropTypes.number,
    merchantName: PropTypes.string,
    categoryId: PropTypes.number,
    categoryFullPath: PropTypes.string,
    request_notes: PropTypes.string,
    review_notes: PropTypes.string,
    hasActiveRule: PropTypes.bool
  }),

  onOkHandler: PropTypes.func.isRequired,
  onRejectHandler: PropTypes.func,

  isRequest: PropTypes.bool,
  isReadOnly: PropTypes.bool
}
